import React, { useState } from 'react';
import { InspectionResult, InspectionChecklistItem, DefectItem, CreateInspectionDto } from '../../types/inspection';
import { Button } from '../ui/Button';
import { InspectionChecklist } from './InspectionChecklist';
import { ReturnToVendorModal } from './ReturnToVendorModal';
import { Camera, Send, X } from 'lucide-react';

interface InspectionFormProps {
  workOrderId: string;
  complaintId: string;
  onSubmit: (data: CreateInspectionDto) => void;
  onCancel: () => void;
}

const DEFAULT_CHECKLIST: InspectionChecklistItem[] = [
  { item_id: 'CHK-001', label: 'Work completed as per scope', checked: false },
  { item_id: 'CHK-002', label: 'Area cleaned after work', checked: false },
  { item_id: 'CHK-003', label: 'All tools removed', checked: false },
  { item_id: 'CHK-004', label: 'System tested and operational', checked: false },
  { item_id: 'CHK-005', label: 'Client walkthrough completed', checked: false },
];

export const InspectionForm: React.FC<InspectionFormProps> = ({
  workOrderId,
  complaintId,
  onSubmit,
  onCancel,
}) => {
  const [result, setResult] = useState<InspectionResult | null>(null);
  const [checklist, setChecklist] = useState<InspectionChecklistItem[]>(DEFAULT_CHECKLIST);
  const [notes, setNotes] = useState('');
  const [defects, setDefects] = useState<DefectItem[]>([]);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!result) newErrors.result = 'Select an inspection result';
    if (result === 'fail' && defects.length === 0) newErrors.defects = 'Add at least one defect when failing inspection';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      work_order_id: workOrderId,
      complaint_id: complaintId,
      result: result!,
      checklist,
      notes,
      photo_urls: [],
      defects,
    });
  };

  const handleReturnToVendor = (data: { defect_description: string; severity: 'minor' | 'major' | 'critical'; corrective_action: string; reason: string; remarks: string }) => {
    const newDefect: DefectItem = {
      defect_id: `DEF-${Date.now()}`,
      description: data.defect_description,
      severity: data.severity,
      corrective_action: data.corrective_action,
    };
    setDefects(prev => [...prev, newDefect]);
    setShowReturnModal(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div>
          <h3 className="text-base font-semibold text-slate-100">Work Order Inspection</h3>
          <p className="text-xs text-slate-400 mt-0.5">{workOrderId}</p>
        </div>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-200 p-1 rounded-lg hover:bg-slate-800">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 text-xs">
        <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
          <p className="text-[10px] text-slate-500">Inspector</p>
          <p className="font-semibold text-slate-200">Suresh (Supervisor)</p>
        </div>
        <div className="p-3 bg-slate-950 rounded-lg border border-slate-800">
          <p className="text-[10px] text-slate-500">Inspection Date</p>
          <p className="font-semibold text-slate-200">{new Date().toLocaleDateString()}</p>
        </div>
      </div>

      <InspectionChecklist items={checklist} onChange={setChecklist} />

      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-300">Inspection Result *</p>
        <div className="flex gap-3">
          {(['pass', 'fail', 'partial'] as InspectionResult[]).map(r => (
            <button
              key={r}
              type="button"
              onClick={() => setResult(r)}
              className={`flex-1 p-3 rounded-lg border text-xs font-semibold capitalize transition-colors ${
                result === r
                  ? r === 'pass'
                    ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400'
                    : r === 'fail'
                    ? 'bg-rose-500/10 border-rose-500/50 text-rose-400'
                    : 'bg-amber-500/10 border-amber-500/50 text-amber-400'
                  : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'
              }`}
            >
              {r}
            </button>
          ))}
        </div>
        {errors.result && <p className="text-xs text-rose-400">{errors.result}</p>}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-300">Inspection Notes</p>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={3}
          placeholder="Describe the inspection findings..."
          className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none resize-none"
        />
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-slate-300">Photos</p>
          <span className="text-[10px] text-slate-500">(placeholder — upload feature coming in Phase 10)</span>
        </div>
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-2 bg-slate-950 border border-dashed border-slate-700 rounded-lg text-xs text-slate-400 hover:border-sky-500/50 hover:text-sky-400 transition-colors"
        >
          <Camera className="w-4 h-4" /> Upload Inspection Photos
        </button>
      </div>

      {defects.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-300">Defects ({defects.length})</p>
          {defects.map(d => (
            <div key={d.defect_id} className="p-3 bg-slate-950 border border-slate-800 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
                  d.severity === 'critical' ? 'bg-rose-500/10 text-rose-400' :
                  d.severity === 'major' ? 'bg-amber-500/10 text-amber-400' :
                  'bg-sky-500/10 text-sky-400'
                }`}>{d.severity}</span>
                <button
                  onClick={() => setDefects(prev => prev.filter(dd => dd.defect_id !== d.defect_id))}
                  className="text-slate-500 hover:text-rose-400 text-xs"
                >Remove</button>
              </div>
              <p className="text-xs text-slate-300">{d.description}</p>
              <p className="text-[10px] text-slate-400 mt-1">Action: {d.corrective_action}</p>
            </div>
          ))}
        </div>
      )}

      {result === 'fail' && defects.length === 0 && errors.defects && (
        <p className="text-xs text-rose-400">{errors.defects}</p>
      )}

      <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800 justify-end">
        {result === 'fail' && (
          <Button variant="danger" onClick={() => setShowReturnModal(true)} icon={<Send className="w-4 h-4" />}>
            Return to Vendor
          </Button>
        )}
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} isLoading={false} icon={<Send className="w-4 h-4" />}>
          Submit Inspection
        </Button>
      </div>

      <ReturnToVendorModal
        isOpen={showReturnModal}
        onClose={() => setShowReturnModal(false)}
        onSubmit={handleReturnToVendor}
      />
    </div>
  );
};
